import { useState } from 'react'
import { LogOut, Scale, ShieldCheck, UserPlus, Users } from 'lucide-react'
import { useRetentionReport } from '../index.js'
import type { RetentionMonth } from '../index.js'
import { EndpointHint, Skeleton } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

const SPANS = [6, 12, 24] as const
type Span = (typeof SPANS)[number]

function monthLabel(month: string, form: 'short' | 'long'): string {
  const date = new Date(`${month}-01T00:00:00Z`)
  return form === 'short'
    ? date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
    : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function Stat({ icon, value, label, sub, tone = 'plain' }: { icon: React.ReactNode; value: string; label: string; sub?: string; tone?: 'good' | 'warn' | 'plain' }) {
  const toneClass = tone === 'good' ? 'text-green' : tone === 'warn' ? 'text-coral' : 'text-ink'
  return (
    <div className="flex items-start gap-3 rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0">
        <div className={`text-xl font-black leading-tight ${toneClass}`}>{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
        {sub && <div className="text-[10px] text-ink-mid">{sub}</div>}
      </div>
    </div>
  )
}

const CHART = { height: 200, top: 14, bottom: 30 }

/**
 * One column per month: those already on the roster (marine) with the
 * month's hires stacked on top (green), and the month's leavers hanging
 * below the baseline (coral) - so growth reads upward and loss downward,
 * on one scale. Each column carries its month in words for a screen
 * reader and on hover.
 */
function RetentionChart({ months }: { months: readonly RetentionMonth[] }) {
  const up = Math.max(1, ...months.map((month) => month.current + month.hired))
  const down = Math.max(0, ...months.map((month) => month.left))
  const plot = CHART.height - CHART.top - CHART.bottom
  const unit = plot / (up + down)
  const baseline = CHART.top + up * unit
  const width = 100 / months.length
  // Too many labels to read at 24 months: name every other one.
  const labelEvery = months.length > 12 ? 2 : 1

  return (
    <div>
      <div className="relative" style={{ height: CHART.height }}>
        <div aria-hidden="true" className="absolute inset-x-0 border-t border-ink/30" style={{ top: baseline }} />
        <ul className="absolute inset-0 flex" aria-label="Hires and leavers by month">
          {months.map((month, index) => {
            const description = `${monthLabel(month.month, 'long')}: ${month.current} already on the roster, ${month.hired} hired, ${month.left} left, ${month.headcount} at month end`
            return (
              <li key={month.month} className="relative h-full" style={{ width: `${width}%` }} title={description} aria-label={description}>
                <div aria-hidden="true" className="absolute inset-x-0 mx-auto flex w-[62%] max-w-[34px] flex-col justify-end" style={{ top: CHART.top, height: up * unit }}>
                  {month.hired > 0 && <div className="mb-px text-center text-[9px] font-bold leading-none text-green">+{month.hired}</div>}
                  <div className="rounded-t-sm bg-green" style={{ height: month.hired * unit }} />
                  <div className={`bg-marine ${month.hired === 0 ? 'rounded-t-sm' : ''}`} style={{ height: month.current * unit }} />
                </div>
                <div aria-hidden="true" className="absolute inset-x-0 mx-auto w-[62%] max-w-[34px]" style={{ top: baseline }}>
                  <div className="rounded-b-sm bg-coral" style={{ height: month.left * unit }} />
                  {month.left > 0 && <div className="mt-px text-center text-[9px] font-bold leading-none text-coral">−{month.left}</div>}
                </div>
                {index % labelEvery === 0 && (
                  <div aria-hidden="true" className="absolute inset-x-0 bottom-0 text-center text-[9px] text-ink-mid">
                    {monthLabel(month.month, 'short')}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-ink-mid">
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-marine" /> Current (already on the roster)
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-green" /> Hired that month
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-coral" /> Left that month
        </li>
      </ul>
    </div>
  )
}

/**
 * The facility's retention report on the Reporting tab: the span in bold
 * strokes (hired, left, net, on the roster, retained), the month-by-month
 * chart of current against hired against left, and the same figures as a
 * table. Read from `GET /facilities/{facilityId}/reports/retention`, which
 * counts the hire and leave dates on the roster.
 */
export function RetentionReportCard({ facilityId }: { facilityId: string }) {
  const [span, setSpan] = useState<Span>(12)
  const query = useRetentionReport(facilityId, span)

  return (
    <section className="rounded-lg border border-marine/20 bg-white p-4" aria-label="Retention report">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm font-black text-ink">
            Retention
            <EndpointHint>GET /facilities/{'{facilityId}'}/reports/retention</EndpointHint>
          </div>
          <div className="text-[11px] text-ink-mid">Hires and leavers month by month, from the hire and leave dates on the roster.</div>
        </div>
        <div role="group" aria-label="Months shown" className="inline-flex rounded-md border border-marine/25 bg-canvas p-0.5">
          {SPANS.map((months) => (
            <button key={months} type="button" aria-pressed={span === months} onClick={() => setSpan(months)} className={`rounded px-2.5 py-1 text-[10.5px] font-bold ${span === months ? 'bg-marine text-white shadow-sm' : 'text-ink-mid hover:text-ink'}`}>
              {months} months
            </button>
          ))}
        </div>
      </div>

      {query.isPending ? (
        <Skeleton />
      ) : query.isError ? (
        <LoadFailure size="xs" message="The retention report could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <Stat icon={<Users className="h-5 w-5" />} value={String(query.data.totals.current)} label="On the roster" sub="today" />
            <Stat icon={<UserPlus className="h-5 w-5" />} value={String(query.data.totals.hired)} label="Hired" sub={`last ${span} months`} tone={query.data.totals.hired > 0 ? 'good' : 'plain'} />
            <Stat icon={<LogOut className="h-5 w-5" />} value={String(query.data.totals.left)} label="Left" sub={`last ${span} months`} tone={query.data.totals.left > 0 ? 'warn' : 'good'} />
            <Stat icon={<Scale className="h-5 w-5" />} value={`${query.data.totals.net > 0 ? '+' : ''}${query.data.totals.net}`} label="Net change" sub="hired less left" tone={query.data.totals.net > 0 ? 'good' : query.data.totals.net < 0 ? 'warn' : 'plain'} />
            <Stat
              icon={<ShieldCheck className="h-5 w-5" />}
              value={query.data.totals.retentionPct === null ? '—' : `${query.data.totals.retentionPct}%`}
              label="Retained"
              sub={`of those here ${span} months ago`}
              tone={query.data.totals.retentionPct === null ? 'plain' : query.data.totals.retentionPct >= 90 ? 'good' : query.data.totals.retentionPct < 75 ? 'warn' : 'plain'}
            />
          </div>

          <RetentionChart months={query.data.months} />

          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                  <th className="py-1 pr-3 font-bold">Month</th>
                  <th className="py-1 pr-3 text-right font-bold">Current</th>
                  <th className="py-1 pr-3 text-right font-bold">Hired</th>
                  <th className="py-1 pr-3 text-right font-bold">Left</th>
                  <th className="py-1 pr-3 text-right font-bold">At month end</th>
                  <th className="py-1 text-right font-bold">Retained</th>
                </tr>
              </thead>
              <tbody>
                {[...query.data.months].reverse().map((month) => (
                  <tr key={month.month} className="border-t border-marine/10 text-ink">
                    <td className="py-1.5 pr-3 font-semibold">{monthLabel(month.month, 'long')}</td>
                    <td className="py-1.5 pr-3 text-right">{month.current}</td>
                    <td className={`py-1.5 pr-3 text-right ${month.hired > 0 ? 'font-bold text-green' : 'text-ink-mid'}`}>{month.hired > 0 ? `+${month.hired}` : '0'}</td>
                    <td className={`py-1.5 pr-3 text-right ${month.left > 0 ? 'font-bold text-coral' : 'text-ink-mid'}`}>{month.left > 0 ? `−${month.left}` : '0'}</td>
                    <td className="py-1.5 pr-3 text-right font-bold">{month.headcount}</td>
                    <td className="py-1.5 text-right">{month.retentionPct === null ? '—' : `${month.retentionPct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] leading-relaxed text-ink-mid">
            Current is who was already on the roster entering the month. A last day still to come is not counted as leaving yet.
            {query.data.unknownHireDates > 0 &&
              ` ${query.data.unknownHireDates} ${query.data.unknownHireDates === 1 ? 'worker has' : 'workers have'} no hire date on file and ${query.data.unknownHireDates === 1 ? 'is' : 'are'} counted as here throughout, never as a hire.`}
          </p>
        </div>
      )}
    </section>
  )
}
